<template>
  <div id="app">

    <Header
      v-bind:navigation="navigation"
      :menu-open="overlayMenuOpen"
      @toggle-menu="overlayMenuOpen = !overlayMenuOpen"
    />
    <OverlayMenu
      v-bind:navigation="navigation"
      :open="overlayMenuOpen"
      @close="overlayMenuOpen = false"
    />

    <!-- HOME -->
    <HomeSection />

    <!-- ABOUT US -->
    <Section :title="sections.getTitle('about')" :sub_title="sections.getSubtitle('about')" id="about_us">
      <AboutUsSection
        v-if="content.hasAbout.value"
        v-bind:content="content.about.value"
        v-bind:backgound_image="getAboutBackgroundImage()"
      />
      <div v-else style="padding: 20px; text-align: center; color: #666;">
        <p v-if="content.aboutLoading.value">Lade About-Daten...</p>
        <p v-else>About-Daten werden geladen...</p>
      </div>
    </Section>

    <!-- APARTMENTS -->
    <Section
      show_selector
      :title="sections.getTitle('apartments')"
      :sub_title="sections.getSubtitle('apartments')"
      id="apartments"
      :selectedApartment="selectedApartment"
      :apartments="apartments.apartments.value"
      @update:selectedApartment="onApartmentChange"
    >
      <ApartmentsSection
        v-if="getApartmentData(selectedApartment)"
        v-bind:selected_apartment="selectedApartment"
        v-bind:apartments="apartments.apartments.value"
        v-bind:content="getApartmentData(selectedApartment)"
        v-bind:backgound_image="getBackgroundImageByIdentifier('apartment_' + selectedApartment)"
        @update:selectedApartment="(slug) => onApartmentChange(slug, 'apartments')"
      />
      <div v-else style="padding: 20px; text-align: center; color: #666;">
        <p v-if="apartments.loading.value">Lade Apartment-Daten...</p>
        <p v-else>Apartment "{{ selectedApartment }}" nicht gefunden.</p>
      </div>
    </Section>

    <!-- PRICING -->
    <Section 
      show_selector 
      :title="sections.getTitle('prices')"
      :sub_title="sections.getSubtitle('prices')"
      id="pricing"
      :selectedApartment="selectedApartment"
      :apartments="apartments.apartments.value"
      @update:selectedApartment="onApartmentChange"
    >
      <PricingsSection
        v-if="prices.hasPrices.value && getApartmentData(selectedApartment)"
        v-bind:prices="prices.getByApartmentSlug(selectedApartment)"
        v-bind:apartment="getApartmentData(selectedApartment)"
      />
      <div v-else style="padding: 20px; text-align: center; color: #666;">
        <p v-if="prices.loading.value">Lade Preisdaten...</p>
        <p v-else-if="!prices.hasPrices.value">Keine Preisdaten verfügbar.</p>
        <p v-else>Apartment "{{ selectedApartment }}" nicht gefunden.</p>
      </div>
    </Section>

    <Paralax :imageId="parallaxImages.getImageId(1)" />

    <!-- RATINGS -->
    <Section 
      show_selector 
      :title="sections.getTitle('reviews')"
      :sub_title="sections.getSubtitle('reviews')"
      id="ratings"
      :selectedApartment="selectedApartment"
      :apartments="apartments.apartments.value"
      @update:selectedApartment="onApartmentChange"
    >
      <RatingsSection
        v-if="getApartmentData(selectedApartment) && ratingsReady"
        ref="ratingsSectionRef"
        v-bind:ratings="selectedApartmentReviews"
        v-bind:appartment_id="getApartmentData(selectedApartment)?._id"
      />
      <div v-else-if="reviews.loading.value || apartments.loading.value" style="padding: 40px; text-align: center; color: #666;">
        <p>Lade Bewertungen...</p>
      </div>
      <div v-else style="padding: 40px; text-align: center; color: #666;">
        <p>Wählen Sie ein Apartment aus, um Bewertungen zu sehen.</p>
      </div>
    </Section>

    <Paralax :imageId="parallaxImages.getImageId(2)" />

    <!-- GALLERY -->
    <Section 
      show_selector 
      :title="sections.getTitle('gallery')"
      :sub_title="sections.getSubtitle('gallery')"
      id="gallery"
      :selectedApartment="selectedApartment"
      :apartments="apartments.apartments.value"
      @update:selectedApartment="onApartmentChange"
    >
      <GallerySection
        v-if="getApartmentData(selectedApartment) && gallery.getByApartmentSlug.value(selectedApartment).length > 0"
        identifier="gallery"
        v-bind:images="gallery.getByApartmentSlug.value(selectedApartment)"
      />
      <div v-else-if="gallery.loading.value" style="padding: 40px; text-align: center; color: #666;">
        <p>Lade Galerie...</p>
      </div>
      <div v-else-if="getApartmentData(selectedApartment)" style="padding: 40px; text-align: center; color: #666;">
        <p>Für dieses Apartment sind keine Bilder verfügbar.</p>
      </div>
      <div v-else style="padding: 40px; text-align: center; color: #666;">
        <p>Wählen Sie ein Apartment aus, um die Galerie zu sehen.</p>
      </div>
    </Section>

    <Paralax :imageId="parallaxImages.getImageId(3)" />

    <!-- IMPRESSIONS -->
    <Section :title="sections.getTitle('impressions')" :sub_title="sections.getSubtitle('impressions')" id="impressions">
      <GallerySection 
        v-if="impressions.hasImpressions.value"
        identifier="impressions" 
        v-bind:images="impressions.impressions.value" 
      />
      <div v-else-if="impressions.loading.value" style="padding: 40px; text-align: center; color: #666;">
        <p>Lade Impressionen...</p>
      </div>
      <div v-else style="padding: 40px; text-align: center; color: #666;">
        <p>Keine Impressionen verfügbar.</p>
      </div>
    </Section>

    <!-- SERVICE AND NOTES -->
    <Section
      :title="sections.getTitle('notices')"
      :sub_title="sections.getSubtitle('notices')"
      id="notices"
    >
      <ServiceAndNotesSection
        v-if="content.hasNotices.value"
        v-bind:content="content.notices.value"
        v-bind:backgound_image="getNoticesBackgroundImage()"
      />
      <div v-else style="padding: 20px; text-align: center; color: #666;">
        <p v-if="content.noticesLoading.value">Lade Hinweise...</p>
        <p v-else>Hinweise werden geladen...</p>
      </div>
    </Section>

    <!-- CONTACT -->
    <Section :title="sections.getTitle('contact')" :sub_title="sections.getSubtitle('contact')" id="contact_us">
      <ContactSection />
    </Section>

    <!--FOOTER-->
    <Footer />

    <ContentPopUp
      @onClose="content_popup.is_open = false"
      v-bind:is_open="content_popup.is_open"
      size="full"
      :title="content_popup.title"
    >
      <template #content>
        <div v-html="content_popup.content"></div>
      </template>
    </ContentPopUp>

    <BookingRequestModal
      v-if="getApartmentData(selectedApartment)"
      :open="bookingModalOpen"
      :apartment="getApartmentData(selectedApartment)"
      :apartments="apartments.apartments.value"
      :initial-check-in="bookingInitialCheckIn"
      :initial-check-out="bookingInitialCheckOut"
      @close="closeBookingModal"
      @update:apartment="(slug) => onApartmentChange(slug)"
    />

    <BookingStickyCta />
  </div>
</template>

<script>
import { onMounted, onUnmounted, nextTick, ref, watch, computed } from 'vue'
import { useApiStore } from './stores/apiStore.js'
import { useApi, useApartments, usePrices, useReviews, useContent, useImpressions, useGallery, useSections, useParallaxImages } from './composables/index.js'

import Header from "./components/Header.vue";
import Footer from "./components/Footer.vue";
import OverlayMenu from "./components/OverlayMenu.vue";
import ContentPopUp from "./components/misc/PopUp.vue";
import Paralax from "./components/misc/Paralax.vue";
import Section from "./components/sections/Section.vue";
import HomeSection from "./components/sections/Home.vue";
import AboutUsSection from "./components/sections/AboutUs.vue";
import ApartmentsSection from "./components/sections/Apartments.vue";
import PricingsSection from "./components/sections/Pricing.vue";
import RatingsSection from "./components/sections/Ratings.vue";
import GallerySection from "./components/sections/Gallery.vue";
import ServiceAndNotesSection from "./components/sections/ServiceAndNotes.vue";
import ContactSection from "./components/sections/Contact.vue";
import BookingRequestModal from "./components/misc/BookingRequestModal.vue";
import BookingStickyCta from "./components/misc/BookingStickyCta.vue";
import {
  captureScrollAnchor,
  compensateScrollAfterLayout
} from './lib/scrollAnchor.js'
import { refreshLegacyLayout } from './lib/layoutRefresh.js'
import { useBookingModal } from './composables/useBookingModal.js'

export default {
  name: "App",
  components: {
    Section,
    Header,
    Footer,
    OverlayMenu,
    ContentPopUp,
    Paralax,
    HomeSection,
    AboutUsSection,
    ApartmentsSection,
    PricingsSection,
    RatingsSection,
    GallerySection,
    ServiceAndNotesSection,
    ContactSection,
    BookingRequestModal,
    BookingStickyCta,
  },
  setup() {
    // Neue API-Integration mit Composables
    const api = useApi()
    const apiStore = useApiStore()
    const apartments = useApartments()
    const prices = usePrices()
    const reviews = useReviews()
    const content = useContent()
    const impressions = useImpressions()
    const gallery = useGallery()
    const sections = useSections()
    const parallaxImages = useParallaxImages()
    const {
      bookingModalOpen,
      bookingInitialCheckIn,
      bookingInitialCheckOut,
      closeBookingModal
    } = useBookingModal()
    
    const selectedApartment = ref('moewennest')
    const ratingsSectionRef = ref(null)

    const selectedApartmentReviews = computed(() =>
      apiStore.getReviewsByApartmentSlug(selectedApartment.value)
    )

    const ratingsReady = computed(() =>
      !reviews.loading.value && !apartments.loading.value
    )

    const onApartmentChange = (slug, sectionId) => {
      if (slug === selectedApartment.value) return

      const anchorEl = sectionId ? document.getElementById(sectionId) : null
      const anchor = anchorEl ? captureScrollAnchor(anchorEl) : null

      selectedApartment.value = slug

      if (anchor) {
        compensateScrollAfterLayout(anchor)
      }
    }

    onMounted(async () => {
      try {
        await api.loadInitialData()
      } catch (error) {
        console.error('Failed to load initial data:', error)
      }
    })

    // Watcher um selected_apartment zu aktualisieren wenn Apartments geladen werden
    watch(() => apartments.apartments.value, (newApartments) => {
      if (newApartments.length > 0) {
        // Prüfe ob der aktuelle selectedApartment existiert
        const exists = newApartments.some(apt => apt.slug === selectedApartment.value)
        if (!exists) {
          // Setze das erste verfügbare Apartment
          selectedApartment.value = newApartments[0].slug
        }
      }
    })

    // Funktion um Apartment-Daten aus dem Store zu holen
    const getApartmentData = (slug) => {
      return apartments.getBySlug(slug)
    }

    // Funktion um Background Image zu finden
    const getBackgroundImageByIdentifier = (identifier) => {
      return content.getBackgroundImageByIdentifier(identifier)
    }

    // Spezielle Funktion für About-Hintergrundbild
    const getAboutBackgroundImage = () => {
      const aboutData = content.about.value
      
      if (aboutData?.image) {
        return { 
          image: aboutData.image, 
          position: 'center center' 
        }
      } else {
        return getBackgroundImageByIdentifier('about_us')
      }
    }

    // Spezielle Funktion für Notices-Hintergrundbild
    const getNoticesBackgroundImage = () => {
      const noticesData = content.notices.value
      
      if (noticesData?.image) {
        return { 
          image: noticesData.image, 
          position: 'center center' 
        }
      } else {
        return getBackgroundImageByIdentifier('services_and_notes')
      }
    }

    const openNewRating = () => {
      ratingsSectionRef.value?.openNewRating?.()
    }

    // Moderne Layout-Neuberechnung mit ResizeObserver
    const layoutObserver = ref(null)
    
    const initLayoutObserver = () => {
      if (!window.ResizeObserver) return
      // Mobile: Adressleiste triggert ständig ResizeObserver → Parallax-Reflows beim Scrollen
      if (window.matchMedia('(max-width: 767px)').matches) return

      layoutObserver.value = new ResizeObserver(() => {
        // Debounce für bessere Performance
        clearTimeout(layoutObserver.value.timeout)
        layoutObserver.value.timeout = setTimeout(() => {
          refreshLegacyLayout()
        }, 100)
      })
      
      // Beobachte wichtige Container
      const containers = document.querySelectorAll('section, .bg, .img-holder')
      containers.forEach(container => {
        layoutObserver.value.observe(container)
      })
    }

    // Layout-Fix nur wenn wirklich nötig
    const recalculateLayout = () => {
      requestAnimationFrame(() => {
        refreshLegacyLayout()
      })
    }

    onMounted(() => {
      // Warte bis DOM vollständig gerendert ist
      nextTick(() => {
        initLayoutObserver()
        recalculateLayout()
      })
    })

    // Cleanup
    onUnmounted(() => {
      if (layoutObserver.value) {
        layoutObserver.value.disconnect()
      }
    })

    // Nur einmal triggern wenn alle kritischen Daten geladen sind
    let hasTriggeredFinalLayout = false
    watch([
      () => api.initialized.value,
      () => content.hasAbout.value,
      () => impressions.hasImpressions.value
    ], ([initialized, hasAbout, hasImpressions]) => {
      if (initialized && hasAbout && hasImpressions && !hasTriggeredFinalLayout) {
        hasTriggeredFinalLayout = true
        setTimeout(recalculateLayout, 200)
      }
    })

    return {
      // Neue API-Integration
      api,
      apartments,
      prices,
      reviews,
      content,
      impressions,
      gallery,
      sections,
      parallaxImages,
      selectedApartment,
      selectedApartmentReviews,
      ratingsReady,
      onApartmentChange,
      getApartmentData,
      getBackgroundImageByIdentifier,
      getAboutBackgroundImage,
      getNoticesBackgroundImage,
      ratingsSectionRef,
      openNewRating,
      recalculateLayout,
      bookingModalOpen,
      bookingInitialCheckIn,
      bookingInitialCheckOut,
      closeBookingModal
    }
  },
  data() {
    return {
      content_popup: {
        title: "",
        content: "",
        is_open: false,
      },
      overlayMenuOpen: false,
      navigation: [
        {
          label: "Home",
          key: "home",
        },
        {
          label: "Über Uns",
          key: "about_us",
        },
        {
          label: "Appartements",
          key: "apartments",
        },
        {
          label: "Preise",
          key: "pricing",
        },
        {
          label: "Galerie",
          key: "gallery",
        },
        {
          label: "Bewertungen",
          key: "ratings",
        },
        {
          label: "Kontakt",
          key: "contact_us",
        },
      ]
    };
  },
};
</script>